import React, { useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const ADMIN_KEY = "kidsgenie_admin_2024";

const AdminFixAccount = () => {
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [courses, setCourses] = useState(['AI', 'Coding']);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const checkUser = async () => {
    setLoading(true);
    setResult(null);
    try {
      const response = await axios.get(`${API}/api/admin/check-user/${email}?admin_key=${ADMIN_KEY}`);
      setResult({ type: 'info', data: response.data });
    } catch (error) {
      setResult({ type: 'error', message: error.response?.data?.detail || 'Error checking user' });
    }
    setLoading(false);
  };

  const resetPassword = async () => {
    if (!newPassword) {
      setResult({ type: 'error', message: 'Please enter a new password' });
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const response = await axios.post(
        `${API}/api/admin/reset-password?email=${email}&new_password=${newPassword}&admin_key=${ADMIN_KEY}`
      );
      setResult({ type: 'success', message: response.data.message });
    } catch (error) {
      setResult({ type: 'error', message: error.response?.data?.detail || 'Error resetting password' });
    }
    setLoading(false);
  };

  const addSubscription = async () => {
    setLoading(true);
    setResult(null);
    try {
      const response = await axios.post(`${API}/api/admin/add-subscription`, {
        email,
        courses,
        admin_key: ADMIN_KEY
      });
      setResult({ type: 'success', data: response.data });
    } catch (error) {
      setResult({ type: 'error', message: error.response?.data?.detail || 'Error adding subscription' });
    }
    setLoading(false);
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1e3a8a 0%, #312e81 100%)',
      padding: '40px 20px'
    }}>
      <div style={{
        maxWidth: '600px',
        margin: '0 auto',
        background: 'white',
        borderRadius: '16px',
        padding: '40px',
        boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
      }}>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '8px', color: '#1e3a8a' }}>
          🔧 Admin Account Fix
        </h1>
        <p style={{ color: '#6b7280', marginBottom: '30px' }}>
          Use this page to fix user accounts after payment issues
        </p>

        {/* Email Input */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
            User Email:
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter user email (e.g., user@example.com)"
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '2px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '16px'
            }}
          />
        </div>

        {/* Step 1: Check User */}
        <div style={{
          background: '#f0f9ff',
          border: '2px solid #0ea5e9',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '16px'
        }}>
          <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#0369a1', marginBottom: '12px' }}>
            Step 1: Check if Account Exists
          </h3>
          <button
            onClick={checkUser}
            disabled={!email || loading}
            style={{
              background: '#0ea5e9',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: email && !loading ? 'pointer' : 'not-allowed',
              opacity: email && !loading ? 1 : 0.5
            }}
          >
            {loading ? '⏳ Checking...' : '🔍 Check User'}
          </button>
        </div>

        {/* Step 2: Reset Password */}
        <div style={{
          background: '#fef3c7',
          border: '2px solid #f59e0b',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '16px'
        }}>
          <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#b45309', marginBottom: '12px' }}>
            Step 2: Reset Password (if needed)
          </h3>
          <input
            type="text"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Enter new password"
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '2px solid #fcd34d',
              borderRadius: '8px',
              fontSize: '16px',
              marginBottom: '12px'
            }}
          />
          <button
            onClick={resetPassword}
            disabled={!email || !newPassword || loading}
            style={{
              background: '#f59e0b',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: email && newPassword && !loading ? 'pointer' : 'not-allowed',
              opacity: email && newPassword && !loading ? 1 : 0.5
            }}
          >
            {loading ? '⏳ Resetting...' : '🔑 Reset Password'}
          </button>
        </div>

        {/* Step 3: Add Subscription */}
        <div style={{
          background: '#d1fae5',
          border: '2px solid #10b981',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '20px'
        }}>
          <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#047857', marginBottom: '12px' }}>
            Step 3: Add Course Subscription
          </h3>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <input
                type="checkbox"
                checked={courses.includes('AI')}
                onChange={(e) => {
                  if (e.target.checked) setCourses([...courses, 'AI']);
                  else setCourses(courses.filter(c => c !== 'AI'));
                }}
              />
              <span style={{ fontWeight: '500' }}>AI Course</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                checked={courses.includes('Coding')}
                onChange={(e) => {
                  if (e.target.checked) setCourses([...courses, 'Coding']);
                  else setCourses(courses.filter(c => c !== 'Coding'));
                }}
              />
              <span style={{ fontWeight: '500' }}>Coding Course</span>
            </label>
          </div>
          <button
            onClick={addSubscription}
            disabled={!email || courses.length === 0 || loading}
            style={{
              background: '#10b981',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: email && courses.length > 0 && !loading ? 'pointer' : 'not-allowed',
              opacity: email && courses.length > 0 && !loading ? 1 : 0.5
            }}
          >
            {loading ? '⏳ Adding...' : '✅ Add Subscription'}
          </button>
        </div>

        {/* Result Display */}
        {result && (
          <div style={{
            padding: '16px',
            borderRadius: '8px',
            background: result.type === 'error' ? '#fef2f2' : result.type === 'success' ? '#f0fdf4' : '#f0f9ff',
            border: `2px solid ${result.type === 'error' ? '#ef4444' : result.type === 'success' ? '#22c55e' : '#3b82f6'}`
          }}>
            <h4 style={{
              fontWeight: '600',
              marginBottom: '8px',
              color: result.type === 'error' ? '#dc2626' : result.type === 'success' ? '#16a34a' : '#2563eb'
            }}>
              {result.type === 'error' ? '❌ Error' : result.type === 'success' ? '✅ Success' : 'ℹ️ Result'}
            </h4>
            {result.message && <p style={{ color: '#374151' }}>{result.message}</p>}
            {result.data && (
              <pre style={{
                background: '#f3f4f6',
                padding: '12px',
                borderRadius: '6px',
                fontSize: '14px',
                overflow: 'auto',
                whiteSpace: 'pre-wrap'
              }}>
                {JSON.stringify(result.data, null, 2)}
              </pre>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminFixAccount;
